import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  TableContainer,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import * as Yup from "yup";

import CustomButton from "../../Components/CustomButton";
import ConfirmDeleteModal from "../../Components/shared/Modals/ConfirmDeleteModal";
import ModalForms from "../../Components/Simple Purchase/ModalForms";
import EditModal from "../../Components/shared/Modals/EditModal";
import CommonTable from "../../Components/shared/CommonTable/CommonTable";
import {
  getContacts,
  deleteContact,
  createNewContact,
  getContactById,
  updateContact,
} from "../../redux/slices/contactSlice";

const ContactListing = ({
  getContactList,
  deleteContact,
  createContact,
  getContactsById,
  updateContactById,
}) => {
  const {
    contactsListLoading,
    deleteContactLoading,
    updateContactLoading,
    contactLoading,
  } = useSelector((state) => state.contact);
  const [contacts, setContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [openModal, setOpenModal] = useState({ newContact: false });
  const [modalFormData, setModalFormData] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [editInitialValues, setEditInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const contactFormFields = [
    {
      key: "firstName",
      label: "First Name",
      type: "text",
      placeholder: "Enter first name",
      width: 12,
    },
    {
      key: "lastName",
      label: "Last Name",
      type: "text",
      placeholder: "Enter last name",
      width: 12,
    },
    {
      key: "email",
      label: "Email",
      type: "email",
      placeholder: "Enter email",
      width: 12,
    },
    {
      key: "phone",
      label: "Phone Number",
      type: "text",
      placeholder: "Enter phone number",
      width: 12,
    },
  ];

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .min(10, "Must be 10 digits"),
  });

  const fetchData = useCallback(async () => {
    const res = await getContactList();
    setContacts(res?.payload?.data || []);
  }, [getContactList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddContactModal = () => {
    setOpenModal({ newContact: true });
    setModalFormData([
      {
        key: "firstName",
        label: "First Name",
        type: "text",
        placeholder: "Enter name",
        width: { xs: "100%", md: "49%" },
      },
      {
        key: "lastName",
        label: "Last Name",
        type: "text",
        placeholder: "Enter name",
        width: { xs: "100%", md: "49%" },
      },
      {
        key: "jobTitle",
        label: "Job Title",
        type: "text",
        placeholder: "Enter job title",
        width: { xs: "100%", md: "49%" },
      },
      {
        key: "fax",
        label: "Fax",
        type: "text",
        placeholder: "Enter fax",
        width: { xs: "100%", md: "49%" },
      },
      {
        key: "phone",
        label: "Phone Number",
        type: "number",
        placeholder: "Enter phone",
        width: { xs: "100%", md: "49%" },
        minLength: 10,
        maxLength: 10,
        required: true,
      },
      {
        key: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter email",
        width: { xs: "100%", md: "49%" },
        required: true,
      },
      {
        key: "website",
        label: "Website",
        type: "text",
        placeholder: "Enter website",
        width: "100%",
      },
      {
        key: "comments",
        label: "Comments",
        type: "textField",
        placeholder: "Enter comments",
        width: "100%",
      },
    ]);
  };

  const handleCreateContact = async (values) => {
    const response = await createContact(values);
    if (createNewContact.fulfilled.match(response)) {
      setOpenModal({ newContact: false });
      fetchData();
    }
  };

  const handleEditContact = async (id) => {
    const res = await getContactsById({ id });
    const contact = res?.payload;
    if (contact) {
      setEditInitialValues({
        // firstName: contact.firstName,
        // lastName: contact.lastName,
        // email: contact.email,
        // phone: contact.phone,
        firstName: contact?.firstName,
        lastName: contact?.lastName || "",
        preferredName:
          contact?.preferredName ||
          `${contact?.firstName || ""} ${contact?.lastName || ""}`,
        email: contact?.email,
        phone: contact?.phone,
        cell_phone: contact?.cell_phone || "", // if 'fax' is what you meant for cell
        job_title: contact?.job_title || "", // correct casing
        organisation_notifications: contact?.organisation_notifications ?? true,
        type: contact?.type || "Supplier",
        organisations: contact?.organisations || [],
      });
      setSelectedContactId(id);
      setEditModalOpen(true);
    }
  };

  const handleUpdateContact = async (values) => {
    const res = await updateContactById({ id: selectedContactId, ...values });
    if (updateContact.fulfilled.match(res)) {
      setEditModalOpen(false);
      fetchData();
    }
  };

  const openDeleteModal = (id) => {
    setSelectedContactId(id);
    setShowModal(true);
  };

  const handleDeleteConfirmed = async () => {
    await deleteContact({ id: selectedContactId });
    setShowModal(false);
    fetchData();
  };

  const columns = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone Number" },
  ];

  const renderActions = (row) => (
    <>
      <IconButton onClick={() => handleEditContact(row.id)}>
        <Edit />
      </IconButton>
      <IconButton color="error" onClick={() => openDeleteModal(row?.id)}>
        <Delete />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ paddingX: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          paddingY: { xs: 1, md: 2 },
          paddingX: { xs: 1.5, md: 3 },
          borderRadius: 2,
          mt: 1,
          flexWrap: "wrap",
          rowGap: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Supplier Contacts
        </Typography>
        <CustomButton
          text="Add Contact"
          bgcolor="#95ABEB"
          color="black"
          onClick={openAddContactModal}
        />
      </Box>

      {contactsListLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : contacts.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography variant="h6" color="text.secondary">
            No data found
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            overflowX: "auto",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            border: "1px solid #e0e0e0",
            "& thead": {
              backgroundColor: "#f9fafb",
              "& th": {
                fontWeight: 600,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                textWrap: "nowrap",
                color: "#333",
              },
            },
          }}
        >
          <CommonTable
            columns={columns}
            data={contacts}
            renderActions={renderActions}
          />
        </TableContainer>
      )}

      <ConfirmDeleteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        handleDelete={handleDeleteConfirmed}
        isLoading={deleteContactLoading}
        heading="Delete Contact"
        description="Are you sure you want to delete this contact? This action cannot be undone."
      />

      <ModalForms
        open={openModal.newContact}
        data={modalFormData}
        setOpen={setOpenModal}
        name="Add Supplier Contact"
        handleSave={handleCreateContact}
        isLoading={contactLoading}
      />

      <EditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateContact}
        initialValues={editInitialValues}
        validationSchema={validationSchema}
        fields={contactFormFields}
        title="Edit Contact"
        isLoading={updateContactLoading}
      />
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getContactList: (data) => dispatch(getContacts(data)),
  getContactsById: (data) => dispatch(getContactById(data)),
  deleteContact: (data) => dispatch(deleteContact(data)),
  createContact: (data) => dispatch(createNewContact(data)),
  updateContactById: (data) => dispatch(updateContact(data)),
});

export default connect(null, mapDispatchToProps)(ContactListing);
